import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { api } from '../api/api';

// Оптимізація: Виносимо статику за межі компонента
const ITEMS_PER_PAGE = 5;
const sanitize = (str: string) => str.replace(/<[^>]*>?/gm, '').trim();

// Окремий мемоїзований компонент для картки
const ReviewCard = memo(({ item, activeTab }: { item: any, activeTab: string }) => (
  <article className="item-card">
    {activeTab === 'qna' ? (
      <div className="qna-wrapper">
        <div className="question-box">
          <div className="user-label">Запит від: {sanitize(item.user_name)}</div>
          <div className="text">{sanitize(item.question)}</div>
        </div>
        {item.answer && (
          <div className="answer-box">
            <div className="doctor-label">👨‍⚕️ ВІДПОВІДЬ</div>
            <div className="text">{sanitize(item.answer)}</div>
          </div>
        )}
      </div>
    ) : (
      <div className="feedback-box">
        <div className="quote-mark">“</div>
        <div className="feedback-header">
          <span className="feedback-name">{sanitize(item.user_name)}</span>
          <span className="date-label">
            {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Верифіковано'}
          </span>
        </div>
        <div className="text">{sanitize(item.question)}</div>
      </div>
    )}
  </article>
));

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'qna' | 'feedback'>('qna');
  const [question, setQuestion] = useState('');
  const [name, setName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // --- Стан для кастомного модального вікна ---
  const [modal, setModal] = useState<{
    show: boolean;
    title: string;
    message: string;
  }>({ show: false, title: '', message: '' });

  const showAlert = (title: string, message: string) => {
    setModal({ show: true, title, message });
  };

  const closeModal = () => setModal(prev => ({ ...prev, show: false }));
  // -------------------------------------------

  useEffect(() => {
    let isMounted = true;
    api.getReviews().then(data => {
      if (isMounted) setReviews(data || []);
    });
    return () => { isMounted = false; };
  }, []);

  const handleTabChange = useCallback((tab: 'qna' | 'feedback') => {
    setActiveTab(tab);
    setCurrentPage(1);
  }, []);

  const submit = async () => {
    const cleanName = sanitize(name);
    const cleanQuestion = sanitize(question);

    if (!cleanName || !cleanQuestion) {
      showAlert('Увага', 'Будь ласка, заповніть усі поля');
      return;
    }

    try {
      await api.createReview({
        user_name: cleanName,
        question: cleanQuestion,
        is_feedback: activeTab === 'feedback' 
      });
      showAlert('Дякуємо!', 'Ваше повідомлення відправлено на модерацію.');
      setName('');
      setQuestion('');
    } catch (err) {
      showAlert('Помилка', 'Виникла помилка при відправці повідомлення.');
    }
  };

  const filteredItems = useMemo(() => {
    return reviews
      .filter(r => r.is_approved)
      .filter(r => activeTab === 'qna' ? r.answer : !r.answer);
  }, [reviews, activeTab]);

  const currentItems = useMemo(() => {
    return filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  return (
    <div className="reviews-page">
      <style>{styles}</style>
      
      {/* КАСТОМНЕ МОДАЛЬНЕ ВІКНО */}
      {modal.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h4 style={{ marginTop: 0, color: '#2d5a50', fontFamily: 'Montserrat' }}>{modal.title}</h4>
            <p style={{ color: '#666', fontSize: '15px', marginBottom: '25px', fontFamily: 'Montserrat' }}>{modal.message}</p>
            <button onClick={closeModal} style={modalPrimaryBtnStyle}>ОК</button>
          </div>
        </div>
      )}

      <div className="blob-bg-1" />
      <div className="blob-bg-2" />

      <div className="reviews-container">
        <header className="reviews-header">
          <h2 className="title">Центр підтримки пацієнтів</h2>
          <div className="divider" />
          <p className="subtitle">Ваш комфорт та здоров'я — наше натхнення</p>
        </header>

        <nav className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'qna' ? 'active' : ''}`}
            onClick={() => handleTabChange('qna')}
          >
            <span className="icon">❓</span> Питання
          </button>
          <button 
            className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => handleTabChange('feedback')}
          >
            <span className="icon">⭐</span> Відгуки
          </button>
        </nav>

        <section className="content-list">
          {currentItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🍃</div>
              <p>Тут поки що порожньо.</p>
            </div>
          ) : (
            currentItems.map((r, idx) => (
              <ReviewCard key={r.id || idx} item={r} activeTab={activeTab} />
            ))
          )}
        </section>

        {totalPages > 1 && (
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => { setCurrentPage(i + 1); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
                className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        <section className="form-section">
          <h3 className="form-title">
            {activeTab === 'qna' ? 'Задати питання лікарю' : 'Залишити відгук'}
          </h3>
          <div className="form-grid">
            <input
              placeholder="Ваше імʼя"
              value={name}
              onChange={e => setName(e.target.value)}
              className="styled-field"
              autoComplete="name"
            />
            <textarea
              placeholder={activeTab === 'qna' ? "Опишіть вашу ситуацію..." : "Поділіться вашим досвідом..."}
              value={question}
              onChange={e => setQuestion(e.target.value)}
              className="styled-field textarea-field"
            />
            <button onClick={submit} className="submit-btn">
              Надіслати повідомлення
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

// --- Стилі Модального Вікна ---
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
  justifyContent: 'center', alignItems: 'center', zIndex: 10000,
  backdropFilter: 'blur(3px)'
};

const modalContentStyle: React.CSSProperties = {
  background: '#fff', padding: '30px', borderRadius: '20px',
  maxWidth: '400px', width: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  textAlign: 'center', animation: 'slideUp 0.3s ease-out'
};

const modalPrimaryBtnStyle = {
  padding: '12px 35px', background: '#1abc9c', color: '#fff',
  border: 'none', borderRadius: '12px', cursor: 'pointer', 
  fontWeight: '700', fontFamily: 'Montserrat'
};

const styles = `
  .reviews-page {
    --primary: #1abc9c;
    --dark: #1e2d2a;
    --light-bg: #f0f9f7;
    --white: #ffffff;
    --border-strong: #c2ded7;
    --spacing: clamp(15px, 4vw, 30px);
    background-color: var(--light-bg);
    min-height: 100vh;
    padding: clamp(40px, 10vw, 80px) 15px;
    font-family: 'Montserrat', sans-serif;
    position: relative;
    overflow-x: hidden;
  }

  .reviews-page * { box-sizing: border-box; max-width: 100%; }
  .reviews-container { position: relative; z-index: 2; max-width: 850px; margin: 0 auto; }
  .reviews-header { text-align: center; margin-bottom: 40px; }
  .title { font-size: clamp(22px, 5vw, 36px); font-weight: 800; color: var(--dark); margin: 0 0 10px; }
  .divider { width: 50px; height: 4px; background: var(--primary); margin: 0 auto 15px; border-radius: 2px; }
  .subtitle { font-size: clamp(14px, 2.5vw, 16px); color: #556b67; }

  .tab-navigation {
    display: flex; gap: 8px; justify-content: center;
    background: #e4f2ef; padding: 6px; border-radius: 50px;
    border: 1px solid var(--border-strong);
    width: fit-content; margin: 0 auto 40px;
  }

  .tab-btn {
    padding: 10px 20px; border: none; border-radius: 40px;
    background: transparent; color: #556b67; cursor: pointer;
    font-weight: 600; font-size: 14px; transition: 0.3s;
    display: flex; align-items: center; gap: 6px;
    white-space: nowrap;
  }
  .tab-btn.active { background: var(--primary); color: var(--white); box-shadow: 0 4px 12px rgba(26,188,156,0.2); }

  .content-list { display: flex; flex-direction: column; gap: 20px; min-height: 200px; }
  .empty-state { text-align: center; padding: 40px; color: #88a09b; }
  .empty-icon { font-size: 40px; margin-bottom: 10px; }

  .item-card { animation: slideUp 0.4s ease-out forwards; will-change: transform, opacity; }

  .question-box, .feedback-box {
    background: var(--white); 
    padding: var(--spacing);
    border-radius: 20px; 
    border: 2px solid var(--border-strong);
    box-shadow: 0 4px 12px rgba(45, 90, 80, 0.08);
    word-wrap: break-word;
    position: relative;
  }

  .user-label, .feedback-name { font-weight: 700; color: var(--primary); margin-bottom: 8px; display: block; }
  .date-label { font-size: 12px; color: #88a09b; }

  .answer-box {
    margin-left: 10%; margin-top: 12px;
    background: #eef9f7; padding: var(--spacing);
    border-radius: 20px; 
    border: 1px solid var(--border-strong);
    border-left: 5px solid var(--primary);
    box-shadow: 0 4px 10px rgba(0,0,0,0.03);
  }

  .doctor-label { font-weight: 800; font-size: 12px; color: var(--dark); margin-bottom: 5px; opacity: 0.8; }
  .quote-mark { 
    position: absolute; top: 10px; right: 20px; 
    font-size: 60px; color: var(--primary); opacity: 0.1; 
    font-family: serif; line-height: 1;
  }

  .form-section {
    background: var(--dark); padding: var(--spacing);
    border-radius: 25px; color: var(--white); margin-top: 50px;
    box-shadow: 0 10px 30px rgba(30, 45, 42, 0.2);
  }
  
  .form-title { margin-top: 0; margin-bottom: 20px; font-size: clamp(18px, 4vw, 24px); }
  .form-grid { display: flex; flex-direction: column; gap: 15px; width: 100%; }

  .styled-field {
    width: 100%; 
    padding: 15px 20px; border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.2); 
    background: rgba(255,255,255,0.08);
    color: var(--white); font-size: 16px; outline: none;
    transition: 0.2s;
    display: block;
    font-family: 'Montserrat', sans-serif;
  }
  .styled-field:focus { border-color: var(--primary); background: rgba(255,255,255,0.12); }
  .textarea-field { min-height: 120px; resize: vertical; }

  .submit-btn {
    width: 100%; background: var(--primary); color: var(--white);
    border: none; padding: 18px; border-radius: 12px; font-weight: 700;
    cursor: pointer; transition: 0.3s; font-size: 16px;
    font-family: 'Montserrat', sans-serif;
  }
  .submit-btn:hover { background: #16a085; transform: translateY(-2px); }

  .pagination { display: flex; justify-content: center; gap: 8px; margin-top: 30px; }
  .page-btn {
    width: 40px; height: 40px; border-radius: 10px; border: 2px solid var(--border-strong);
    cursor: pointer; font-weight: 700; background: var(--white); color: var(--dark);
    transition: 0.2s;
  }
  .page-btn.active { background: var(--primary); color: var(--white); border-color: var(--primary); }

  .blob-bg-1, .blob-bg-2 { position: absolute; border-radius: 50%; filter: blur(80px); z-index: 1; opacity: 0.05; pointer-events: none; }
  .blob-bg-1 { top: -5%; right: -5%; width: 300px; height: 300px; background: var(--primary); }
  .blob-bg-2 { bottom: -5%; left: -5%; width: 250px; height: 250px; background: var(--dark); }

  @keyframes slideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  @media (max-width: 600px) {
    .reviews-page { padding: 20px 10px; }
    .answer-box { margin-left: 5%; }
    .tab-navigation { width: 100%; }
    .tab-btn { flex: 1; justify-content: center; padding: 10px 5px; }
  }
`;